// src/hooks/useTeamMembers.js

import { useState, useEffect, useRef } from 'react';
import { db } from '../firebase';
import {
  collection,
  query,
  where,
  onSnapshot,
  getDocs,
  doc,
  getDoc
} from 'firebase/firestore';
import { getAllAssistants } from '../services/openaiService';

/**
 * Hook para obtener:
 * - Miembros reales MKT (documentos en users con accessCode == userData.accessCode)
 * - Miembros ficticios del usuario actual (userData.equipo)
 * - Contar las conversaciones de cada miembro si estamos en /messages
 */
const useTeamMembers = (currentUser, isMessagesPage) => {
  const [teamMembers, setTeamMembers] = useState([]);          // Reales + Ficticios
  const [teamMembersWithCounts, setTeamMembersWithCounts] = useState([]); // Para mostrar conteo
  const unsubscribeMembersListenersRef = useRef([]);
  const assistantOwnerCountsRef = useRef({});

  useEffect(() => {
    if (!currentUser) return;

    const fetchTeamMembers = async () => {
      try {
        // 1) Obtener datos del usuario actual
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userSnap = await getDoc(userDocRef);

        if (!userSnap.exists()) {
          console.error('No se encontró el documento del usuario actual.');
          return;
        }
        const userData = userSnap.data();

        // 2) Miembros ficticios => SOLO del array "equipo" del usuario actual
        const fictitiousMembers = Array.isArray(userData.equipo)
          ? userData.equipo
          : [];

        // 3) Miembros reales => todos los docs en "users" con el mismo accessCode,
        //    PERO sin mezclar su propio array "equipo" (solo datos del doc).
        let realMktMembers = [];
        const { accessCode } = userData;
        if (accessCode) {
          const usersRef = collection(db, 'users');
          const q = query(usersRef, where('accessCode', '==', accessCode));
          const snapshot = await getDocs(q);

          realMktMembers = snapshot.docs.map((docSnap) => {
            const d = docSnap.data();
            return {
              id: docSnap.id,
              type: 'real',
              nombre: d.name || '',       // o d.nombre, según guardes
              apellido: d.apellido || '',
              cargo: d.cargo || '',
            };
          });
        }

        // 4) Mapear los ficticios del usuario actual => IDs "member0", "member1", etc.
        const mappedFict = fictitiousMembers.map((fict, index) => ({
          id: `member${index}`,
          type: 'fictitious',
          nombre: fict.nombre,
          apellido: fict.apellido,
          cargo: fict.cargo || '',
        }));

        // 5) Unir real + ficticios en un solo array
        const combined = [...realMktMembers, ...mappedFict];
        setTeamMembers(combined);

      } catch (error) {
        console.error('Error al obtener miembros del equipo:', error);
      }
    };

    fetchTeamMembers();
  }, [currentUser]);

  // ==========================================
  //    Efecto para contar conversaciones (si /messages)
  // ==========================================
  useEffect(() => {
    if (!isMessagesPage || teamMembers.length === 0) {
      setTeamMembersWithCounts([]);
      return;
    }

    // Limpiar listeners previos
    unsubscribeMembersListenersRef.current.forEach((unsub) => unsub());
    unsubscribeMembersListenersRef.current = [];

    assistantOwnerCountsRef.current = {};

    const fetchConversationCounts = async () => {
      try {
        // Obtener todos los asistentes del usuario actual
        const assistantsData = await getAllAssistants(currentUser.uid);

        // Por cada asistente, suscribirse a los threads donde conversationowner coincida
        for (const assistant of assistantsData) {
          for (const member of teamMembers) {
            const ownerName = `${member.nombre} ${member.apellido}`.trim();
            // Variantes que podrían estar en la DB (por espacios, etc.)
            const ownerVariants = [ownerName, `${ownerName} `];

            const threadsCollectionRef = collection(db, `threads_${assistant.id}`);
            const q = query(threadsCollectionRef, where('conversationowner', 'in', ownerVariants));

            const unsubscribe = onSnapshot(
              q,
              (querySnapshot) => {
                const newCountsForThisAssistant =
                  assistantOwnerCountsRef.current[assistant.id] || {};

                // Actualizar conteo
                newCountsForThisAssistant[ownerName] = querySnapshot.size;
                assistantOwnerCountsRef.current[assistant.id] = newCountsForThisAssistant;

                // Combinar conteos de todos los asistentes en un solo objeto
                const combinedCounts = {};
                for (const aId in assistantOwnerCountsRef.current) {
                  const assistantCounts = assistantOwnerCountsRef.current[aId];
                  for (const ownerKey in assistantCounts) {
                    combinedCounts[ownerKey] =
                      (combinedCounts[ownerKey] || 0) + assistantCounts[ownerKey];
                  }
                }

                // Crear array final con la propiedad conversationCount
                const membersWithCounts = teamMembers.map((m) => {
                  const oName = `${m.nombre} ${m.apellido}`.trim();
                  return {
                    ...m,
                    conversationCount: combinedCounts[oName] || 0,
                  };
                });

                setTeamMembersWithCounts(membersWithCounts);
              },
              (error) => {
                console.error(`Error al escuchar threads_${assistant.id}:`, error);
              }
            );

            // Guardar el unsubscribe
            unsubscribeMembersListenersRef.current.push(unsubscribe);
          }
        }
      } catch (error) {
        console.error('Error en fetchConversationCounts:', error);
      }
    };

    fetchConversationCounts();

    return () => {
      // Desuscribirse de todos
      unsubscribeMembersListenersRef.current.forEach((unsub) => unsub());
      unsubscribeMembersListenersRef.current = [];
    };
  }, [isMessagesPage, teamMembers, currentUser]);

  // Devolvemos 2 arrays: sin counts (teamMembers) y con counts (teamMembersWithCounts)
  return { teamMembers, teamMembersWithCounts };
};

export default useTeamMembers;
